import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

// Use anon key here (not service role). Anonymous access goes through the
// `get_public_merchant(slug, token)` RPC, which returns only safe public
// fields and only when both slug and scan_token match.
function getPublicClient() {
  const url = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
  const key =
    process.env.SUPABASE_PUBLISHABLE_KEY ||
    process.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
    process.env.SUPABASE_ANON_KEY;
  if (!url || !key) {
    throw new Error("Missing SUPABASE_URL or SUPABASE_PUBLISHABLE_KEY");
  }
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

/**
 * Loads a merchant for the public /tack/$slug page.
 * Accepts a "slug-token" combined segment. Splits on the last "-" so
 * branded slugs containing dashes still work (e.g. "joes-pizza-k7p2x9").
 *
 * Returns null when the token is missing/wrong — the route then renders 404.
 */
export const loadTackMerchant = createServerFn({ method: "GET" })
  .inputValidator((input: { slugWithToken: string }) => input)
  .handler(async ({ data }) => {
    const raw = data.slugWithToken;
    const empty = { merchant: null, env: "live" as const, scanEnabled: true, preTrial: false };
    const lastDash = raw.lastIndexOf("-");
    if (lastDash <= 0 || lastDash === raw.length - 1) {
      return empty;
    }
    const slug = raw.slice(0, lastDash);
    const token = raw.slice(lastDash + 1);

    // Token must be exactly 6 alphanumerics — cheap guard against probing.
    if (!/^[a-z0-9]{6}$/i.test(token)) {
      return empty;
    }

    const client = getPublicClient();
    const { data: rows, error } = await client
      .rpc("get_public_merchant", { _slug: slug, _token: token.toLowerCase() });

    if (error || !rows || rows.length === 0) {
      return empty;
    }

    const merchant = rows[0]!;

    // Has the merchant activated their rewards account (trial started or paid)?
    // Never accept a feedback submission we can't reward — the customer would
    // hand over a phone number and get nothing. Demo merchants always pass.
    // Environment comes from the request host (preview → sandbox, production →
    // live), not a build-time flag, so test subscriptions unlock the preview
    // only.
    const { getPaymentEnvFromRequest } = await import("@/lib/payment-env.server");
    const env = getPaymentEnvFromRequest();
    const { data: statusRows } = await client.rpc("public_merchant_scan_status", {
      _merchant_id: merchant.id,
      _env: env,
    });
    const status = statusRows?.[0];

    return {
      merchant,
      env,
      // Fail open if the status lookup hiccups — never break a live QR code.
      scanEnabled: status ? status.has_access : true,
      preTrial: status ? status.pre_trial : false,
    };
  });
